import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useTenantLogo } from '../../contexts/TenantBrandingContext';
import { useAuth } from '../../contexts/AuthContext';
import { api, tokenManager } from '../../lib/api';

export const EmailVerificationPage: React.FC = () => {
  const { logoUrl, logoAlt } = useTenantLogo();
  const { refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [isVerifying, setIsVerifying] = useState(true);
  const [isAutoLoggingIn, setIsAutoLoggingIn] = useState(false);
  const [verificationResult, setVerificationResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [email, setEmail] = useState<string>('');

  useEffect(() => {
    const token = searchParams.get('token');
    const emailParam = searchParams.get('email');
    
    if (emailParam) {
      setEmail(emailParam);
    }
    
    if (token) {
      verifyEmail(token);
    } else {
      setVerificationResult({
        success: false,
        message: 'No verification token provided'
      });
      setIsVerifying(false);
    }
  }, [searchParams]);

  const verifyEmail = async (token: string) => {
    try {
      const response = await api.auth.verifyEmail(token);
      setVerificationResult({
        success: response.data.verified,
        message: response.data.message
      });
      
      // FIRST: Check if we need to set redirect flags (BEFORE attempting auto-login)
      // This ensures flags are set even if auto-login fails
      let shouldSetRedirectFlag = false;
      let planToUseForFlag = 'starter';
      
      if (response.data.verified) {
        console.log('Email verification: Verification successful, checking if redirect flag needed...');
        
        // Try to get plan from pending registration first
        try {
          const pendingRegistration = localStorage.getItem('pending_registration');
          if (pendingRegistration) {
            const registrationData = JSON.parse(pendingRegistration);
            if (registrationData.plan) {
              planToUseForFlag = registrationData.plan;
              console.log('Email verification: Found plan in pending registration:', planToUseForFlag);
            }
          }
        } catch (e) {
          console.warn('Email verification: Failed to read pending registration:', e);
        }
        
        // Check if card is required for trial
        // Use full API URL to avoid CORS/404 issues
        try {
          const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.feedbackgate.com';
          const featuresUrl = `${apiBaseUrl}/api/v1/system/features`;
          console.log('Email verification: Fetching features from:', featuresUrl);
          
          const featuresResponse = await fetch(featuresUrl, { 
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'omit' // System features should be public
          });
          
          if (featuresResponse.ok) {
            const contentType = featuresResponse.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              const features = await featuresResponse.json();
              console.log('Email verification: Features:', features);
              
              if (features.require_card_for_trial) {
                shouldSetRedirectFlag = true;
                console.log('Email verification: Card required for trial - will set redirect flag');
              } else {
                console.log('Email verification: Card not required for trial');
              }
            } else {
              console.warn('Email verification: Features endpoint returned non-JSON response');
              // Assume card is required if we can't check (safer default)
              shouldSetRedirectFlag = true;
              console.log('Email verification: Assuming card required (features check failed)');
            }
          } else {
            console.warn('Email verification: Features endpoint returned status:', featuresResponse.status);
            // Assume card is required if we can't check (safer default)
            shouldSetRedirectFlag = true;
            console.log('Email verification: Assuming card required (features check failed)');
          }
        } catch (e) {
          console.warn('Email verification: Failed to fetch system features:', e);
          // Assume card is required if we can't check (safer default)
          shouldSetRedirectFlag = true;
          console.log('Email verification: Assuming card required (features check failed)');
        }
        
        // Set flag NOW if card is required, before auto-login attempt
        if (shouldSetRedirectFlag) {
          localStorage.setItem('redirect_to_checkout_after_attestation', 'true');
          localStorage.setItem('checkout_plan', planToUseForFlag);
          sessionStorage.setItem('redirect_to_checkout_after_attestation', 'true');
          sessionStorage.setItem('checkout_plan', planToUseForFlag);
          console.log('✅ Email verification: Redirect flag set BEFORE auto-login attempt');
          console.log('  - Plan:', planToUseForFlag);
          console.log('  - Flag verification:', localStorage.getItem('redirect_to_checkout_after_attestation'));
        }
      }
      
      // Check if tokens were returned for auto-login
      if (response.data.verified && response.data.access_token && response.data.refresh_token) {
        setIsAutoLoggingIn(true);
        
        try {
          // Store tokens
          tokenManager.setTokens(response.data.access_token, response.data.refresh_token);
          console.log('Email verification: Tokens stored');
          
          // Fetch user profile and tenant info
          await refreshUser();
          console.log('Email verification: User refreshed');
          
          // Verify we're actually authenticated before proceeding
          const accessToken = tokenManager.getAccessToken();
          if (!accessToken) {
            console.error('Email verification: No access token after refresh, cannot proceed');
            setIsAutoLoggingIn(false);
            return;
          }
          
          // Check billing status and redirect to checkout if needed
          try {
            // Small delay to ensure user/tenant data is fully loaded
            await new Promise(resolve => setTimeout(resolve, 500));
            
            const billingResponse = await api.billing.getInfo();
            const billingInfo = billingResponse.data;
            
            console.log('Email verification: Billing info:', billingInfo);
            
            // Check if billing is incomplete and card is required for trial
            const isIncomplete = billingInfo.status === 'incomplete';
            const isSetupRequired = (billingInfo as any).setup_required === true;
            
            // Update plan from billing info if available (more accurate)
            if (billingInfo.plan) {
              planToUseForFlag = billingInfo.plan;
              console.log('Email verification: Updating plan from billing info:', planToUseForFlag);
              // Update the flag with the correct plan
              if (shouldSetRedirectFlag) {
                localStorage.setItem('checkout_plan', planToUseForFlag);
                sessionStorage.setItem('checkout_plan', planToUseForFlag);
              }
            }
            
            if (isIncomplete || isSetupRequired) {
              console.log('Email verification: Billing is incomplete - ALWAYS setting redirect flag');
              
              // ALWAYS set flag if billing is incomplete (regardless of features check)
              // This ensures the flag is set even if features check failed earlier
              localStorage.setItem('redirect_to_checkout_after_attestation', 'true');
              localStorage.setItem('checkout_plan', planToUseForFlag);
              sessionStorage.setItem('redirect_to_checkout_after_attestation', 'true');
              sessionStorage.setItem('checkout_plan', planToUseForFlag);
              
              // Verify the flag was set
              const flagCheck = localStorage.getItem('redirect_to_checkout_after_attestation');
              const planCheck = localStorage.getItem('checkout_plan');
              const sessionFlagCheck = sessionStorage.getItem('redirect_to_checkout_after_attestation');
              const sessionPlanCheck = sessionStorage.getItem('checkout_plan');
              console.log('✅ Email verification: Redirect flag set/verified after billing check');
              console.log('  - localStorage flag:', flagCheck, 'plan:', planCheck);
              console.log('  - sessionStorage flag:', sessionFlagCheck, 'plan:', sessionPlanCheck);
            } else {
              console.log('Email verification: Billing is not incomplete, status:', billingInfo.status);
              // Clear flag if billing is complete (user doesn't need checkout)
              localStorage.removeItem('redirect_to_checkout_after_attestation');
              sessionStorage.removeItem('redirect_to_checkout_after_attestation');
            }
          } catch (billingError) {
            console.error('Email verification: Failed to check billing status:', billingError);
            // Fall through to normal navigation
          }
          
          // Clear pending registration data
          localStorage.removeItem('pending_registration');
          localStorage.removeItem('redirect_to_checkout');
          
          // Navigate to dashboard - MainLayout will handle any remaining redirects
          navigate('/', { replace: true });
          return; // Exit early - user is being redirected
        } catch (loginError) {
          console.error('Email verification: Auto-login error:', loginError);
          // DO NOT clear flags - keep them for manual login
          // The flag should have been set before auto-login attempt
          console.log('Email verification: Auto-login failed, but keeping redirect flag for manual login');
          // Fall through to show success message with manual login option
          setIsAutoLoggingIn(false);
        }
      } else if (response.data.verified) {
        // Verification succeeded but no tokens - user needs to login manually
        // Set redirect flag if card is required (so manual login can redirect to checkout)
        if (shouldSetRedirectFlag) {
          console.log('Email verification: No tokens, but setting redirect flag for manual login');
          localStorage.setItem('redirect_to_checkout_after_attestation', 'true');
          localStorage.setItem('checkout_plan', planToUseForFlag);
          sessionStorage.setItem('redirect_to_checkout_after_attestation', 'true');
          sessionStorage.setItem('checkout_plan', planToUseForFlag);
          console.log('✅ Email verification: Redirect flag set for manual login');
        }
        console.log('Email verification: Verified but no tokens, user needs to login');
        // Don't set checkout flag here - user needs to login first
      } else if (response.data.verified) {
        // Verification succeeded but no tokens (shouldn't happen normally, but handle gracefully)
        const pendingRegistration = localStorage.getItem('pending_registration');
        if (pendingRegistration) {
          try {
            const registrationData = JSON.parse(pendingRegistration);
            if (registrationData.requires_payment) {
              // Store flag to redirect to checkout after manual login
              localStorage.setItem('redirect_to_checkout', 'true');
            }
          } catch (e) {
            console.warn('Failed to parse pending registration data:', e);
          }
        }
      }
    } catch (error: any) {
      console.error('Email verification error:', error);
      setVerificationResult({
        success: false,
        message: error.response?.data?.detail || 'Email verification failed'
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const resendVerification = async () => {
    if (!email) return;
    
    try {
      await api.auth.resendVerification(email);
      setVerificationResult(prev => prev ? {
        ...prev,
        message: 'Verification email sent successfully. Please check your inbox.'
      } : null);
    } catch (error: any) {
      console.error('Resend verification error:', error);
      setVerificationResult(prev => prev ? {
        ...prev,
        message: error.response?.data?.detail || 'Failed to resend verification email'
      } : null);
    }
  };

  if (isVerifying || isAutoLoggingIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            {logoUrl && (
              <img
                className="mx-auto h-12 w-auto"
                src={logoUrl}
                alt={logoAlt}
              />
            )}
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              {isVerifying ? 'Verifying Email' : 'Logging You In...'}
            </h2>
            <div className="mt-6 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
            <p className="mt-4 text-sm text-gray-600">
              {isVerifying 
                ? 'Please wait while we verify your email address...'
                : 'Setting up your account...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {logoUrl && (
            <img
              className="mx-auto h-12 w-auto"
              src={logoUrl}
              alt={logoAlt}
            />
          )}
          
          {verificationResult?.success ? (
            <>
              <div className="mt-6 flex justify-center">
                <div className="rounded-full bg-green-100 p-3">
                  <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                Email Verified!
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                {verificationResult.message}
              </p>
              {(() => {
                const pendingRegistration = localStorage.getItem('pending_registration');
                const requiresCheckout = pendingRegistration && JSON.parse(pendingRegistration).requires_payment;
                
                return requiresCheckout ? (
                  <p className="mt-4 text-sm text-blue-600">
                    After logging in, you'll be redirected to add your payment method to start your free trial.
                  </p>
                ) : null;
              })()}
              <div className="mt-6">
                <Link
                  to="/login"
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Continue to Login
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="mt-6 flex justify-center">
                <div className="rounded-full bg-red-100 p-3">
                  <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              </div>
              <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                Verification Failed
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                {verificationResult?.message}
              </p>
              
              {email && (
                <div className="mt-6 space-y-4">
                  <button
                    onClick={resendVerification}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Resend Verification Email
                  </button>
                  
                  <Link
                    to="/register"
                    className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Back to Registration
                  </Link>
                </div>
              )}
              
              <div className="mt-6">
                <Link
                  to="/login"
                  className="text-sm text-indigo-600 hover:text-indigo-500"
                >
                  Back to Login
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
